import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { GlassPanel } from "./GlassPanel";
import {
  relatedPolicies,
  POLICY_EFFECTIVE_DATE,
  POLICY_LAST_UPDATED,
} from "@/content/legalPolicies";
import { siteConfig } from "@/config/site";
import "./PolicyLayout.css";

export interface PolicySection {
  id: string;
  label: string;
}

interface PolicyLayoutProps {
  slug: string;
  title: string;
  intro: string;
  sections: PolicySection[];
  children: ReactNode;
}

export function PolicyLayout({ slug, title, intro, sections, children }: PolicyLayoutProps) {
  const related = relatedPolicies(slug);
  return (
    <>
      <section className="container page-hero policy-hero">
        <p className="eyebrow">Legal</p>
        <h1 className="page-hero-title">{title}</h1>
        <p className="page-hero-lede">{intro}</p>
        <dl className="policy-dates">
          <div>
            <dt>Effective date</dt>
            <dd>{POLICY_EFFECTIVE_DATE}</dd>
          </div>
          <div>
            <dt>Last updated</dt>
            <dd>{POLICY_LAST_UPDATED}</dd>
          </div>
        </dl>
      </section>

      <section className="container section policy-body">
        <nav className="policy-toc" aria-label={`${title} table of contents`}>
          <GlassPanel className="policy-toc-panel">
            <h2>On this page</h2>
            <ul>
              {sections.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`}>{section.label}</a>
                </li>
              ))}
            </ul>
          </GlassPanel>
        </nav>
        <div className="prose policy-content">{children}</div>
      </section>

      <section className="container section-tight policy-footer">
        <GlassPanel className="problem-card">
          <h2>Questions about this policy?</h2>
          <p>
            Contact Devory at{" "}
            <a href={`mailto:${siteConfig.contactEmail}`}>{siteConfig.contactEmail}</a> or use the{" "}
            <Link to="/contact">contact form</Link> and select the Legal category.
          </p>
          <h3>Related policies</h3>
          <ul className="policy-related-list">
            {related.map((policy) => (
              <li key={policy.slug}>
                <Link to={policy.path}>{policy.title}</Link>
              </li>
            ))}
            <li>
              <Link to="/legal">All policies</Link>
            </li>
          </ul>
        </GlassPanel>
      </section>
    </>
  );
}
